import { useEffect, useState } from 'react'
import { stateName } from './stateNames'

// Standalone Approvals widget for cross-origin embed via the
// ApprovalsWidget.init() SDK. Semantic <ul> lists inside Shadow DOM. Two
// modes, one layout:
//
//   sport:      US states approved for the sport + its international regions
//               (GET /api/v1/sports/:slug/details)
//   sportsbook: US states where the book is live, its international
//               allow-list, and — for deny-list operators — the regions it
//               is NOT available in (GET /api/v1/sportsbooks/:slug/details)
//
// Owns its own fetch so the SDK only needs the key + apiBase.

export interface SportDetails {
  id: number
  name: string
  slug: string
  approved_states: string[]
  international: string[]
}

export interface SportsbookDetails {
  id: number
  slug: string
  name: string
  active_states: string[]
  international: string[]
  blocked_international: string[]
}

interface Lists {
  us: string[]
  international: string[]
  blocked: string[]
}

interface Props {
  /** Sport slug. One of sport / sportsbook is required. */
  sport?: string
  /** Sportsbook slug (slugified name). */
  sportsbook?: string
  apiBase: string
}

export default function ApprovalsWidget({ sport, sportsbook, apiBase }: Props) {
  const [lists, setLists] = useState<Lists | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const mode: 'sport' | 'sportsbook' = sportsbook ? 'sportsbook' : 'sport'
  const key = sportsbook ?? sport ?? ''

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    if (!key) {
      setError('ApprovalsWidget.init needs `sport` or `sportsbook`')
      setLoading(false)
      return
    }
    const path = mode === 'sportsbook'
      ? `/api/v1/sportsbooks/${encodeURIComponent(key)}/details`
      : `/api/v1/sports/${encodeURIComponent(key)}/details`
    fetch(`${apiBase}${path}`)
      .then(async (res) => {
        if (res.status === 404) {
          if (!cancelled) setError(mode === 'sportsbook' ? `no sportsbook found for "${key}"` : `no sport found for "${key}"`)
          return null
        }
        if (!res.ok) throw new Error(`fetch failed: ${res.status}`)
        return res.json() as Promise<SportDetails | SportsbookDetails>
      })
      .then((d) => {
        if (cancelled || !d) return
        if (mode === 'sportsbook') {
          const b = d as SportsbookDetails
          setLists({ us: b.active_states, international: b.international, blocked: b.blocked_international ?? [] })
        }
        else {
          const s = d as SportDetails
          setLists({ us: s.approved_states, international: s.international, blocked: [] })
        }
      })
      .catch((e) => {
        if (cancelled) return
        setError((e as Error).message)
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [mode, key, apiBase])

  if (loading) return <p className="ad-msg">LOADING…</p>
  if (error) return <p className="ad-msg">⚠ {error}</p>
  if (!lists) return <p className="ad-msg">no approvals data</p>

  const usCount = lists.us.length
  const intlCount = lists.international.length
  const blockedCount = lists.blocked.length

  return (
    <div className={`ew-approvals${mode === 'sportsbook' ? ' is-sportsbook' : ''}`}>
      <section className="ew-section">
        <h3 className="ew-section-head">
          <span className="ew-section-title">United States</span>
          <span className="ew-section-count">{usCount} of 51</span>
        </h3>
        {usCount === 0
          ? <p className="ew-section-empty">none</p>
          : (
              <ul className="ew-list ew-list-us">
                {lists.us.map(code => (
                  <li key={code} title={code}>{stateName(code)}</li>
                ))}
              </ul>
            )}
      </section>

      <section className="ew-section">
        <h3 className="ew-section-head">
          <span className="ew-section-title">International</span>
          <span className="ew-section-count">{intlCount} region{intlCount === 1 ? '' : 's'}</span>
        </h3>
        {intlCount === 0
          ? <p className="ew-section-empty">{blockedCount > 0 ? 'everywhere not listed below' : 'none'}</p>
          : (
              <ul className="ew-list ew-list-intl">
                {lists.international.map(region => (
                  <li key={region}>{region}</li>
                ))}
              </ul>
            )}
      </section>

      {/* Deny-list operators (sportsbook mode only): live everywhere except these. */}
      {blockedCount > 0 && (
        <section className="ew-section ew-section-blocked">
          <h3 className="ew-section-head">
            <span className="ew-section-title">Not available in</span>
            <span className="ew-section-count">{blockedCount} region{blockedCount === 1 ? '' : 's'}</span>
          </h3>
          <ul className="ew-list ew-list-blocked">
            {lists.blocked.map(region => (
              <li key={region}>{region}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
